import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../_metronic/layout/core'
import useAuth from '../../../../hooks/useAuth'
import { getSessionHistory } from '../../SellerQueue/services'
import { SellerServiceSessionItem } from '../../SellerQueue/types'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface SellerCorrelationRow {
  sellerId: number
  sellerLabel: string
  totalSessions: number
  completedSales: number
  pendingCheckout: number
  strictConversionRate: number
  avgPendingAgeHours: number
  criticalPendingCount: number
  pressureScore: number
}

const numberFormatter = new Intl.NumberFormat('pt-BR')
const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
const hourFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const parseDate = (value?: string | null): Date | null => {
  if (!value) {
    return null
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }

  return date
}

const getSellerLabel = (session: SellerServiceSessionItem): string => {
  const fallbackName = `Vendedor ${session.sellerId}`
  const rawName = session.sellerName?.trim()

  return rawName && rawName.length > 0 ? rawName : fallbackName
}

const toPercent = (value: number, total: number): number => {
  if (!total) {
    return 0
  }

  return (value / total) * 100
}

const exportToCSV = (filename: string, data: any[]): void => {
  let csv = 'sep=;\n'
  csv += 'Posição;Vendedor;Atendimentos;Conversão (%);Pending;Críticos 24h+;Aging Médio (h);Score\n'

  data.forEach((row, index) => {
    csv += `${index + 1};"${row.sellerLabel}";${row.totalSessions};${row.strictConversionRate.toFixed(1)};${row.pendingCheckout};${row.criticalPendingCount};${row.avgPendingAgeHours.toFixed(1)};${row.pressureScore.toFixed(1)}\n`
  })

  const bom = '\uFEFF'
  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, data: any[]): void => {
  const rows = data.map((row, index) => ({
    'Posição': index + 1,
    'Vendedor': row.sellerLabel,
    'Atendimentos': row.totalSessions,
    'Conversão (%)': row.strictConversionRate.toFixed(1),
    'Pending': row.pendingCheckout,
    'Críticos 24h+': row.criticalPendingCount,
    'Aging Médio (h)': row.avgPendingAgeHours.toFixed(1),
    'Score': row.pressureScore.toFixed(1),
  }))

  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Conversão x Aging')
  XLSX.writeFile(wb, filename)
}

const exportToPDF = (filename: string, data: any[], summary: any): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  // Header
  pdf.setFontSize(16)
  pdf.text('Relatório de Conversão x Aging', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  const reportDate = new Date().toLocaleString('pt-BR')
  pdf.text(`Relatório gerado em: ${reportDate}`, margin, yPosition)
  yPosition += 12

  // Summary Section
  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Indicadores Consolidados', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  const summaryText = [
    `Total de Vendedores: ${summary.totalSellers} | Pending Total: ${summary.totalPending} | Críticos 24h+: ${summary.totalCritical}`,
    `Conversão Média: ${summary.avgConversion.toFixed(1)}%`,
  ]

  summaryText.forEach((line) => {
    pdf.text(line, margin, yPosition)
    yPosition += 5
  })

  yPosition += 5

  // Seller Table
  const tableData = data.map((row, index) => [
    index + 1,
    row.sellerLabel,
    row.totalSessions,
    row.strictConversionRate.toFixed(1),
    row.pendingCheckout,
    row.criticalPendingCount,
    row.avgPendingAgeHours.toFixed(1),
    row.pressureScore.toFixed(1),
  ])

  autoTable(pdf, {
    startY: yPosition,
    head: [
      [
        'Pos',
        'Vendedor',
        'Atend.',
        'Conv. (%)',
        'Pend.',
        'Críticos',
        'Aging (h)',
        'Score',
      ],
    ],
    body: tableData,
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 8,
      textColor: 50,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    columnStyles: {
      1: { halign: 'left' },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'center' },
      5: { halign: 'center' },
      6: { halign: 'center' },
      7: { halign: 'center' },
    },
  })

  // Footer
  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(
      `Página ${i} de ${pageCount}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    )
  }

  pdf.save(filename)
}

const SellerConversionAgingCorrelationReportPage: FC = () => {
  const { loginInfo } = useAuth()
  const pluginspaceId = Number(loginInfo.pluginspaceId || 0)

  const [filters, setFilters] = useState({
    startDate: toDateInputValue(firstDayOfMonth),
    finalDate: toDateInputValue(today),
  })
  const [loading, setLoading] = useState(false)
  const [sessions, setSessions] = useState<SellerServiceSessionItem[]>([])

  const loadReport = useCallback(async () => {
    if (!pluginspaceId || pluginspaceId <= 0) {
      setSessions([])
      return
    }

    try {
      setLoading(true)
      const data = await getSessionHistory(pluginspaceId, filters.startDate, filters.finalDate)
      setSessions(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters.finalDate, filters.startDate, pluginspaceId])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const sellerRows = useMemo<SellerCorrelationRow[]>(() => {
    const now = Date.now()
    const grouped = new Map<number, SellerCorrelationRow>()

    sessions.forEach((session) => {
      const current = grouped.get(session.sellerId)
      const existing =
        current ||
        {
          sellerId: session.sellerId,
          sellerLabel: getSellerLabel(session),
          totalSessions: 0,
          completedSales: 0,
          pendingCheckout: 0,
          strictConversionRate: 0,
          avgPendingAgeHours: 0,
          criticalPendingCount: 0,
          pressureScore: 0,
        }

      existing.totalSessions += 1

      if (session.outcome === 'sale_completed') {
        existing.completedSales += 1
      }

      if (session.outcome === 'pending_checkout') {
        existing.pendingCheckout += 1
        const referenceDate = parseDate(session.finishedAt) || parseDate(session.startedAt)
        const ageHours = referenceDate ? Math.max(0, (now - referenceDate.getTime()) / 3600000) : 0
        existing.avgPendingAgeHours += ageHours

        if (ageHours >= 24) {
          existing.criticalPendingCount += 1
        }
      }

      grouped.set(session.sellerId, existing)
    })

    return Array.from(grouped.values())
      .map((row) => {
        const strictConversionRate = toPercent(row.completedSales, row.totalSessions)
        const avgPendingAgeHours = row.pendingCheckout ? row.avgPendingAgeHours / row.pendingCheckout : 0

        // Pressure score balances low conversion and pending backlog aging.
        const pressureScore =
          row.pendingCheckout * 8 +
          row.criticalPendingCount * 12 +
          avgPendingAgeHours * 0.8 +
          Math.max(0, 100 - strictConversionRate) * 0.3

        return {
          ...row,
          strictConversionRate,
          avgPendingAgeHours,
          pressureScore,
        }
      })
      .sort((left, right) => right.pressureScore - left.pressureScore)
  }, [sessions])

  const summary = useMemo(() => {
    const totalSellers = sellerRows.length
    const totalPending = sellerRows.reduce((acc, row) => acc + row.pendingCheckout, 0)
    const totalCritical = sellerRows.reduce((acc, row) => acc + row.criticalPendingCount, 0)
    const avgConversion = totalSellers
      ? sellerRows.reduce((acc, row) => acc + row.strictConversionRate, 0) / totalSellers
      : 0

    return {
      totalSellers,
      totalPending,
      totalCritical,
      avgConversion,
    }
  }, [sellerRows])

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Comparação Conversão x Aging',
            path: '/seller/reports/conversion-aging-correlation',
            isActive: true,
          },
        ]}
      >
        Relatório de Conversão x Aging
      </PageTitle>

      <ReportShell
        title='Comparação Conversão x Aging por Vendedor'
        description='Cruza taxa de conversão com pressão de pending checkout para priorizar follow-up comercial por vendedor.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button
              className='btn btn-light-success'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToCSV(`Conversao-Aging-${timestamp}.csv`, sellerRows)
              }}
              disabled={loading || sellerRows.length === 0}
              title='Baixar relatório em CSV'
            >
              <i className='fas fa-file-csv me-2'></i>
              CSV
            </button>
            <button
              className='btn btn-light-info'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToXLSX(`Conversao-Aging-${timestamp}.xlsx`, sellerRows)
              }}
              disabled={loading || sellerRows.length === 0}
              title='Baixar relatório em XLSX'
            >
              <i className='fas fa-file-excel me-2'></i>
              XLSX
            </button>
            <button
              className='btn btn-light-danger'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToPDF(`Conversao-Aging-${timestamp}.pdf`, sellerRows, summary)
              }}
              disabled={loading || sellerRows.length === 0}
              title='Baixar relatório em PDF'
            >
              <i className='fas fa-file-pdf me-2'></i>
              PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-5'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-5'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        {!pluginspaceId ? (
          <div className='alert alert-warning mb-0'>Pluginspace nao identificado para o usuario logado.</div>
        ) : (
          <>
            <div className='row g-4 mb-4'>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Vendedores analisados</div>
                    <div className='fs-2hx fw-bold text-primary'>{numberFormatter.format(summary.totalSellers)}</div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Conversão média</div>
                    <div className='fs-2hx fw-bold text-success'>{percentFormatter.format(summary.avgConversion)}%</div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Pending checkout</div>
                    <div className='fs-2hx fw-bold text-warning'>{numberFormatter.format(summary.totalPending)}</div>
                  </div>
                </div>
              </div>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Criticos (+24h)</div>
                    <div className='fs-2hx fw-bold text-danger'>{numberFormatter.format(summary.totalCritical)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className='card border-0 shadow-sm'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>Matriz de pressão por vendedor</span>
                  <span className='text-gray-600 mt-1'>Score maior indica maior prioridade de ação comercial e follow-up de checkout.</span>
                </div>
              </div>
              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex justify-content-center align-items-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </div>
                ) : sellerRows.length > 0 ? (
                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                          <th>Prioridade</th>
                          <th>Vendedor</th>
                          <th className='text-end'>Atendimentos</th>
                          <th className='text-end'>Conversão</th>
                          <th className='text-end'>Pending checkout</th>
                          <th className='text-end'>Idade média pending</th>
                          <th className='text-end'>Criticos (+24h)</th>
                          <th className='text-end'>Score de pressão</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerRows.map((row, index) => (
                          <tr key={row.sellerId}>
                            <td>#{index + 1}</td>
                            <td className='fw-bold text-dark'>
                              <SellerIdentity label={row.sellerLabel} />
                            </td>
                            <td className='text-end'>{numberFormatter.format(row.totalSessions)}</td>
                            <td className='text-end fw-semibold text-success'>{percentFormatter.format(row.strictConversionRate)}%</td>
                            <td className='text-end fw-semibold text-warning'>{numberFormatter.format(row.pendingCheckout)}</td>
                            <td className='text-end'>{hourFormatter.format(row.avgPendingAgeHours)}h</td>
                            <td className='text-end fw-semibold text-danger'>{numberFormatter.format(row.criticalPendingCount)}</td>
                            <td className='text-end fw-bold'>{hourFormatter.format(row.pressureScore)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='alert alert-light-info mb-0'>Nenhuma sessão encontrada para os filtros selecionados.</div>
                )}
              </div>
            </div>
          </>
        )}
      </ReportShell>
    </>
  )
}

export { SellerConversionAgingCorrelationReportPage }
